// src/utils/sortLocations.js
//
// Slice P2 — Portfolio sort helpers.
//
// Five sort keys were originally scoped (smart / alphabetical / size /
// capacity / newest). "Newest first" was de-scoped at the P2 verification
// gate — see Lesson 39: `fdob` was documented in CLAUDE.md but did not
// exist on the location row in the live server at the time (it was later
// added by LD2/migration 0010, then retired by
// FDOB-TO-ONLINEDATE-CONSOLIDATE-S1 in favor of online_date).
// "Alphabetical" was removed by LSE-LOCATIONS-SORT-REMOVE-ALPHABETICAL:
// the dropdown option was redundant because clicking the Location column
// header in List view already sorts A→Z. The sortAlphabetical helper is
// retained (not removed) because sortLocationsColumn.js depends on it
// for column-header sorting via applyColumnSort("location").
// The dispatch table below is authoritative for what the SortDropdown
// is allowed to render.
//
// Design:
//   - Pure functions, exported individually for unit testing.
//   - All sorters are NON-MUTATING — they take a list and return a new
//     list. Callers (PortfolioView) pass already-ranked + filtered data.
//   - "smart" is the canonical internal default (stage-priority + sqft
//     DESC via rankSites). It is NOT listed in SORT_KEYS / SORT_OPTIONS
//     so it no longer appears in the SortDropdown — it is the automatic
//     first-load sort, not a user-selectable option.
//     It MUST be a non-mutating identity-clone of its input — it does
//     NOT call rankSites itself. PortfolioView ranks ONCE upstream via
//     rankSites(locations) and passes the post-filter subset here;
//     calling rankSites again on a filtered subset would reassign `rank`
//     numbers (e.g. the 4th-ranked card becomes "01" in a build-stage-
//     only view) and break the stable-rank contract LocationList relies
//     on. The verification gate caught this in P2 review — see the unit
//     tests for the "smart preserves rank values" guard.
//   - Numeric sorts (size, capacity) push null/undefined/0 to the end
//     so empty rows never crowd the top of "largest first" / "most
//     seats first" displays. Tiebreak by id ASC for stable rendering.
//   - Alphabetical uses localeCompare with sensitivity:'base' so
//     case + diacritic differences don't fragment the order.

// Public sort keys. The SortDropdown imports this list to render menu
// items so the two surfaces can never drift. Order in this array is
// the order shown in the menu. "smart" (stage-priority) is the
// internal default — it is NOT in this array so it does not appear as
// a user-selectable chip. See DEFAULT_SORT_KEY below.
export const SORT_KEYS = ["size", "capacity"];

export const SORT_OPTIONS = [
  { key: "size",     label: "Size (sq ft)",     chipLabel: "Sq Ft", subtitle: "Largest first" },
  { key: "capacity", label: "Capacity (seats)", chipLabel: "Seats", subtitle: "Most seats first" },
];

// Internal default — stage-priority + sqft DESC via rankSites. Not listed
// in SORT_KEYS / SORT_OPTIONS so it is never shown as a dropdown chip.
export const DEFAULT_SORT_KEY = "smart";

// Tiebreaker — stable id-based order so two rows with equal sort keys
// always render in the same position across re-renders.
function byIdAsc(a, b) {
  return String(a.id || "").localeCompare(String(b.id || ""));
}

// Numeric DESC with null/undefined/0 pushed to the end. Treats any
// non-positive or non-finite value as "missing" so empty fields
// don't crowd the top of "Largest first" / "Most seats first".
function byNumericDescNullsLast(field) {
  return (a, b) => {
    const av = Number(a[field]);
    const bv = Number(b[field]);
    const aMissing = !Number.isFinite(av) || av <= 0;
    const bMissing = !Number.isFinite(bv) || bv <= 0;
    if (aMissing && bMissing) return byIdAsc(a, b);
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (bv !== av) return bv - av;
    return byIdAsc(a, b);
  };
}

export function sortSmart(locations) {
  // Identity-clone. Callers MUST pass pre-ranked data (PortfolioView
  // ranks once upstream via rankSites). Re-ranking the filtered subset
  // here would mutate the `rank` field — see the file header for the
  // P2 regression that motivated this contract.
  return [...locations];
}

export function sortAlphabetical(locations) {
  return [...locations].sort((a, b) => {
    const cmp = String(a.market || "").localeCompare(
      String(b.market || ""),
      undefined,
      { sensitivity: "base" },
    );
    if (cmp !== 0) return cmp;
    return byIdAsc(a, b);
  });
}

export function sortBySize(locations) {
  return [...locations].sort(byNumericDescNullsLast("sqft"));
}

export function sortByCapacity(locations) {
  return [...locations].sort(byNumericDescNullsLast("installedCapacity"));
}

const DISPATCH = {
  smart:    sortSmart,
  size:     sortBySize,
  capacity: sortByCapacity,
};

// Single dispatch entry point — unknown keys fall back to smart so a
// stale localStorage value or a future test typo can never produce
// an empty list.
export function applySort(locations, key) {
  const fn = DISPATCH[key] || DISPATCH[DEFAULT_SORT_KEY];
  return fn(Array.isArray(locations) ? locations : []);
}
